import { useState } from 'react';
import { RefreshCw, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi } from '../../api/coworkApi';
import type { BrandGraph, BrandLook, BrandLookFace } from '../../types/cowork';

type Brand = BrandGraph['brand'];

/**
 * The look read from the brand's own website: colours by role, faces (and their web equivalents), buttons, picture
 * corners and the art direction. "Read again" re-measures the site; "Use these" copies the measured colours, faces and
 * art direction into the editable fields (marked as captured, so a later reading can refresh them until someone edits).
 */
export function SiteLookCard({ brand, brandId, onBrand }: { brand: Brand; brandId?: string; onBrand: (b: Brand) => void }) {
  const [busy, setBusy] = useState(false);
  const look = brand.look;

  const read = async () => {
    setBusy(true);
    try {
      const r = await coworkApi.brands.look(brandId, brand.website || undefined);
      onBrand(r.brand);
      toast.success(r.set.length ? `Look read — updated ${r.set.length} field${r.set.length === 1 ? '' : 's'}` : 'Look read from the website');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read the website');
    } finally { setBusy(false); }
  };

  const adopt = () => {
    if (!look) return;
    const colours = [look.colors.primary, ...look.colors.accents].filter((c): c is string => !!c);
    const fonts = [look.fonts.display?.name, look.fonts.body?.name].filter((f, i, a): f is string => !!f && a.indexOf(f) === i);
    const webFonts = [look.fonts.display?.google, look.fonts.body?.google].filter((f): f is string => !!f);
    const visualStyle = { aesthetic: look.aesthetic, directives: look.directives, visualAvoid: look.visualAvoid, preferredFormats: look.preferredFormats };
    const next: Brand = { ...brand };
    const captured = { ...(brand.captured || {}) };
    const put = <K extends keyof Brand>(k: K, v: Brand[K]) => { next[k] = v; captured[k as string] = JSON.stringify(v); };
    if (colours.length) put('primaryColors', colours);
    if (look.colors.neutrals.length) put('neutralColors', look.colors.neutrals);
    if (fonts.length) put('fonts', fonts);
    if (webFonts.length) put('webFonts', webFonts);
    if (look.aesthetic) put('visualStyle', visualStyle);
    onBrand({ ...next, captured });
    toast.success('Colours, faces and art direction now follow the website');
  };

  const differs = !!look && (
    JSON.stringify([look.colors.primary, ...look.colors.accents].filter(Boolean)) !== JSON.stringify(brand.primaryColors || []) ||
    JSON.stringify([look.fonts.display?.name, look.fonts.body?.name].filter((f, i, a) => !!f && a.indexOf(f) === i)) !== JSON.stringify(brand.fonts || []) ||
    (!!look.aesthetic && brand.visualStyle?.aesthetic !== look.aesthetic)
  );

  return (
    <div className="mb-6 rounded-2xl border border-gray-100 bg-[#fafafa] p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>
            <Globe className="h-3.5 w-3.5 text-[#98a2b3]" /> From the website
          </div>
          <p className="mt-0.5 text-[11px] text-[#98a2b3]">
            {look ? `Measured ${new Date(look.capturedAt).toLocaleDateString()} on ${look.pages.length} page${look.pages.length === 1 ? '' : 's'} · decks and visuals follow this look` : 'Open the site in a browser and measure its faces, colours, buttons and pictures'}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {look && differs && (
            <button onClick={adopt} className="rounded-lg bg-[#101828] px-3 py-1.5 text-[12px] text-white hover:bg-[#1d2939]" style={{ fontWeight: 600 }}>
              Use these
            </button>
          )}
          <button onClick={read} disabled={busy || !brand.website} title={brand.website ? undefined : 'Add the website above first'}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[12px] text-[#344054] hover:bg-gray-50 disabled:opacity-50" style={{ fontWeight: 600 }}>
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> {busy ? 'Reading the site…' : look ? 'Read again' : 'Read the look'}
          </button>
        </div>
      </div>
      {look && (
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="space-y-3">
            <Swatches label="Brand" colours={[look.colors.primary, ...look.colors.accents].filter((c): c is string => !!c)} />
            <Swatches label="Neutrals" colours={look.colors.neutrals} />
            <Face label="Display" face={look.fonts.display} size={22} />
            <Face label="Body" face={look.fonts.body} size={14} />
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-[#667085]">
              {look.button && (
                <span className="inline-flex items-center px-3 py-1 text-[11px]" style={{ background: look.button.fill || 'transparent', color: look.button.text || '#101828', border: look.button.border ? `1px solid ${look.button.border}` : look.button.fill ? 'none' : '1px solid #d0d5dd', borderRadius: Math.min(look.button.radius ?? 0, 999), textTransform: (look.button.case as any) || 'none', fontWeight: look.button.weight || 600 }}>
                  Button
                </span>
              )}
              {look.imageRadius !== undefined && <span>Pictures: {look.imageRadius ? `${look.imageRadius}px corners` : 'square'}</span>}
              {look.imageryShare !== undefined && <span>~{Math.round(look.imageryShare * 100)}% pictures</span>}
            </div>
          </div>
          <div className="space-y-2 text-[12px] leading-relaxed text-[#475467]">
            {look.aesthetic && <p className="text-[#101828]">{look.aesthetic}</p>}
            <Line label="Photography" text={look.photography} />
            <Line label="Colour" text={look.colourUse} />
            <Line label="Type" text={look.typography} />
            <Line label="Devices" text={look.graphicDevices} />
            <Line label="Density" text={look.density} />
          </div>
        </div>
      )}
    </div>
  );
}

function Swatches({ label, colours }: { label: string; colours: string[] }) {
  if (!colours.length) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 text-[11px] text-[#98a2b3]">{label}</span>
      {colours.map((c) => (
        <span key={c} className="flex items-center gap-1">
          <span className="h-5 w-5 rounded-md ring-1 ring-black/10" style={{ background: c }} />
          <span className="font-mono text-[10px] text-[#667085]">{c.toUpperCase()}</span>
        </span>
      ))}
    </div>
  );
}

function Face({ label, face, size }: { label: string; face?: BrandLookFace; size: number }) {
  if (!face) return null;
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-16 text-[11px] text-[#98a2b3]">{label}</span>
      <span style={{ fontFamily: `'${face.name}', '${face.google || ''}', system-ui, sans-serif`, fontSize: size, fontWeight: face.weight || 500, textTransform: (face.case as any) || 'none', letterSpacing: face.tracking ? `${face.tracking}em` : undefined, lineHeight: 1.1 }}>
        {face.name}
      </span>
      {face.google && face.google !== face.name && <span className="text-[10px] text-[#98a2b3]">web: {face.google}</span>}
    </div>
  );
}

function Line({ label, text }: { label: string; text?: string }) {
  if (!text) return null;
  return <p><span className="text-[#98a2b3]">{label} · </span>{text}</p>;
}
